import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/cubit-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Cubit Alternative for UK Builders",
  "description": "Looking for a Cubit (BSS Software) alternative for UK builders? The Proper Tool offers cloud-based AI quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Cubit Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Cubit alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK builders who want cloud-based quoting with AI-powered scope generation rather than desktop-based takeoff software, The Proper Tool is the strongest Cubit alternative. Cubit, sold in the UK via BSS Software, is an Australian desktop estimating product that requires manual configuration for UK rates. The Proper Tool starts at £29/month with a 14-day free trial and no installation required.",
      },
    },
    {
      "@type": "Question",
      "name": "What is Cubit software and who sells it in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cubit is an Australian desktop-based estimating and takeoff software product. In the UK it is sold via BSS Software (bsssoftware.com), which also runs the content site builderestimatingsoftware.co.uk. BSS Software operates as a reseller and implementation partner rather than the original developer of Cubit.",
      },
    },
    {
      "@type": "Question",
      "name": "Is Cubit cloud-based?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Cubit is a desktop software product. It requires installation on a Windows PC and does not have a native cloud or browser-based version. The Proper Tool is entirely cloud-native: no installation is required and it works on any device with a browser.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Cubit have a UK construction pricing database?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cubit is an Australian product and its pricing databases are built around Australian construction rates. UK builders typically need to manually configure the cost library for UK labour and material rates before it produces realistic UK estimates. The Proper Tool has a 31-category UK cost library built in and calibrated for UK residential construction from day one.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I try Cubit before buying?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cubit is sold via a reseller (BSS Software) and does not have a self-serve trial. You need to contact BSS Software to arrange a demonstration and evaluation. The Proper Tool offers a 14-day free trial with no credit card required, so you can test it on real jobs immediately.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  cubit: string;
  cubitState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Cloud-based (no installation)", cubit: "No: desktop software only", cubitState: "no", tpt: "Yes: browser-based", tptState: "yes" },
  { feature: "UK residential construction pricing", cubit: "Australian rates by default", cubitState: "no", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "AI scope generation from description", cubit: "No: manual entry only", cubitState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings automatically", cubit: "Partial: manual takeoff", cubitState: "partial", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Scope completeness validation", cubit: "No", cubitState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Mobile-friendly", cubit: "No: desktop only", cubitState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Labour / materials / plant split", cubit: "Yes (detailed takeoff)", cubitState: "yes", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Self-serve trial", cubit: "No: via reseller only", cubitState: "no", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", cubit: "Reseller-quoted", cubitState: "no", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Time to first quote", cubit: "Installation + significant setup", cubitState: "no", tpt: "Under 10 minutes", tptState: "yes" },
  { feature: "Sold directly", cubit: "No: via BSS Software reseller", cubitState: "no", tpt: "Yes: direct subscription", tptState: "yes" },
  { feature: "Credit card required to trial", cubit: "No self-serve trial available", cubitState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Desktop software in a cloud world",
    desc: "Cubit requires installation on a Windows PC. In an era where builders need to quote from multiple devices, on site, or at home, desktop-only software is a significant constraint. The Proper Tool is entirely cloud-native and works on any device with a browser.",
  },
  {
    title: "Australian pricing database requires UK configuration",
    desc: "Cubit is an Australian product. Its cost library is built around Australian construction rates. UK builders need to manually rebuild rate schedules for UK labour and material costs before estimates are realistic, which takes significant time and effort.",
  },
  {
    title: "No AI, no drawing interpretation, no scope generation",
    desc: "Cubit's estimating is based on manual takeoff from drawings. There is no AI that reads a project description and generates a complete scope, and no automatic extraction of specifications from PDFs. All quantities must be measured and entered manually.",
  },
  {
    title: "Sold via reseller with no self-serve trial",
    desc: "Cubit in the UK is sold through BSS Software as a reseller. There is no self-serve trial or transparent pricing on the BSS Software website. Getting access requires contacting a salesperson and going through an evaluation process.",
  },
];

const STAY_ON_CUBIT = [
  "You are already embedded in the BSS Software / Cubit ecosystem and have invested significant time configuring UK rate schedules.",
  "You specifically need detailed desktop-based measurement takeoff functionality with imperial and metric flexibility.",
  "Your organisation is in Australia where the Cubit pricing database is calibrated for the local market.",
  "You need the depth of a traditional estimating takeoff tool and are comfortable with the desktop workflow.",
];

const SWITCH_REASONS = [
  "You want cloud-based quoting that works on any device without installation.",
  "You want UK-calibrated pricing built in from day one, not an Australian database to rebuild.",
  "You want to generate quotes from descriptions or drawings without hours of manual takeoff.",
  "You want to trial the software immediately on real jobs without going through a reseller.",
  "You want transparent monthly pricing with no installation costs or reseller margins.",
  "You want a 14-day free trial with no credit card, not an arranged demonstration.",
];

const FAQS = [
  {
    q: "What is the best Cubit alternative for UK builders?",
    a: "For UK builders who want cloud-based quoting with AI-powered scope generation rather than desktop takeoff software, The Proper Tool is the strongest Cubit alternative. Cubit is an Australian desktop estimating product sold in the UK via BSS Software and requires manual configuration for UK rates. The Proper Tool starts at £29/month with a 14-day free trial.",
  },
  {
    q: "What is Cubit software and who sells it in the UK?",
    a: "Cubit is an Australian desktop-based estimating and takeoff software product. In the UK it is sold via BSS Software (bsssoftware.com), which operates as a reseller and implementation partner rather than the original developer.",
  },
  {
    q: "Is Cubit cloud-based?",
    a: "No. Cubit is a desktop software product requiring installation on a Windows PC. The Proper Tool is entirely cloud-native: no installation is required and it works on any device with a browser.",
  },
  {
    q: "Does Cubit have a UK construction pricing database?",
    a: "Cubit's pricing databases are built around Australian construction rates. UK builders typically need to manually configure the cost library for UK rates. The Proper Tool has a 31-category UK cost library built in from day one.",
  },
  {
    q: "Can I try Cubit before buying?",
    a: "Cubit is sold via a reseller and does not have a self-serve trial. The Proper Tool offers a 14-day free trial with no credit card required, so you can test it on real jobs immediately.",
  },
];

export default function CubitAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Cubit Alternative for UK Builders: Cloud Quoting Without the Desktop Install | The Proper Tool"
        description="Looking for a Cubit (BSS Software) alternative? The Proper Tool offers cloud-based AI quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo."
        canonical="/cubit-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Cubit Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Cubit Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Cubit is an Australian desktop estimating product sold in the UK via BSS Software. If you want cloud-based, AI-powered quoting without the desktop install or the Australian pricing database, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why UK builders look for a Cubit alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Cubit has genuine depth as a takeoff tool. But UK builders exploring alternatives consistently flag the same friction points:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Cubit: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Cubit</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.cubit} state={row.cubitState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with BSS Software before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Cubit pricing in the UK is not published on the BSS Software website. Costs are quoted through the reseller and vary by licence type and support level. There is no self-serve trial. The Proper Tool starts at £29 per month with all features included and a 14-day free trial with no credit card required.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Cubit (via BSS Software)</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Pricing quoted via reseller, not published</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No self-serve trial, requires arranged demo</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Desktop installation required</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Australian pricing database needs UK configuration</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month, all features included</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No installation: works in any browser</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />UK cost library built in from day one</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on Cubit</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Cubit is a strong desktop estimating tool for a specific type of builder. If the following describes your situation, it may still be the right choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_CUBIT.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons UK builders switch from Cubit to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically from desktop-based manual takeoff to cloud-native AI-powered quoting.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Cloud-based quoting for UK residential builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with BSS Software before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/estimatorxpress-alternative", label: "EstimatorXpress Alternative" },
                { href: "/evaluate-alternative", label: "EValuate Alternative" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
